import type { QueryClient } from '@tanstack/react-query';

import {
  patientPharmaciesQueryDefinition,
  patientPharmacyToRxPharmacy,
  pharmacyByIdQueryDefinition,
  pharmacyToRxPharmacy,
  type RxMedication,
  type RxPharmacy,
  selectMedicationQueryDefinition,
} from '@/entities/rx';
import { logger } from '@/shared/lib';

import { chooseDefaultPatientPharmacy } from './chooseDefaultPatientPharmacy';

export type PrefillPharmacyIssue = 'lookup-failed' | 'not-found';

export type PrefillPharmacyResult =
  | { kind: 'resolved'; pharmacy: RxPharmacy }
  | { kind: 'unresolved'; issue: PrefillPharmacyIssue };

/** What an execute-only prefill could not load. */
export interface RxPrefillIssue {
  medicationFailed: boolean;
  pharmacy: PrefillPharmacyIssue | null;
}

export async function fetchPrefillMedication(
  queryClient: QueryClient,
  dispensableDrugId: number
): Promise<RxMedication | null> {
  try {
    return await queryClient.fetchQuery(
      selectMedicationQueryDefinition({ dispensable_drug_id: dispensableDrugId })
    );
  } catch (err: unknown) {
    logger.warn('rxWizard.prefill.medicationLookupFailed', { dispensableDrugId, err });
    return null;
  }
}

/**
 * The drafted pharmacy when the action names one, otherwise the patient's
 * default saved pharmacy.
 */
export async function resolvePrefillPharmacy(
  queryClient: QueryClient,
  userId: string,
  pharmacyId: number | undefined
): Promise<PrefillPharmacyResult> {
  const requestedPharmacyId = pharmacyId != null && Number.isFinite(pharmacyId) ? pharmacyId : null;
  try {
    const patientPharmacies = await queryClient.fetchQuery(
      patientPharmaciesQueryDefinition(userId)
    );
    let pharmacy: RxPharmacy | null;
    if (requestedPharmacyId != null) {
      const savedMatch = patientPharmacies.find(
        (candidate) => candidate.PharmacyId === requestedPharmacyId
      );
      pharmacy = savedMatch ? patientPharmacyToRxPharmacy(savedMatch) : null;
      pharmacy ??= pharmacyToRxPharmacy(
        await queryClient.fetchQuery(pharmacyByIdQueryDefinition(String(requestedPharmacyId)))
      );
    } else {
      const defaultPatientPharmacy = chooseDefaultPatientPharmacy(patientPharmacies);
      pharmacy = defaultPatientPharmacy
        ? patientPharmacyToRxPharmacy(defaultPatientPharmacy)
        : null;
    }
    if (pharmacy) {
      return { kind: 'resolved', pharmacy };
    }
    logger.warn('rxWizard.prefill.noPharmacyResolved', { userId, requestedPharmacyId });
    return { kind: 'unresolved', issue: 'not-found' };
  } catch (err: unknown) {
    logger.warn('rxWizard.prefill.pharmacyLookupFailed', { userId, pharmacyId, err });
    return { kind: 'unresolved', issue: 'lookup-failed' };
  }
}

export interface RetryRxPrefillArgs {
  queryClient: QueryClient;
  userId: string;
  dispensableDrugId: number;
  pharmacyId: number | undefined;
  issue: RxPrefillIssue;
  hasSelectedPharmacy: () => boolean;
  applyMedication: (medication: RxMedication) => void;
  applyPharmacy: (pharmacy: RxPharmacy) => void;
}

/**
 * Re-runs only the lookups in ``issue``. A pharmacy the clinician selects
 * while the pharmacy lookup is in flight wins over the lookup's result.
 */
export async function retryRxPrefill({
  queryClient,
  userId,
  dispensableDrugId,
  pharmacyId,
  issue,
  hasSelectedPharmacy,
  applyMedication,
  applyPharmacy,
}: RetryRxPrefillArgs): Promise<RxPrefillIssue | null> {
  let medicationFailed = issue.medicationFailed;
  if (medicationFailed) {
    const medication = await fetchPrefillMedication(queryClient, dispensableDrugId);
    if (medication) {
      applyMedication(medication);
      medicationFailed = false;
    }
  }

  let pharmacyIssue = hasSelectedPharmacy() ? null : issue.pharmacy;
  if (pharmacyIssue) {
    const pharmacy = await resolvePrefillPharmacy(queryClient, userId, pharmacyId);
    if (hasSelectedPharmacy()) {
      pharmacyIssue = null;
    } else if (pharmacy.kind === 'resolved') {
      applyPharmacy(pharmacy.pharmacy);
      pharmacyIssue = null;
    } else {
      pharmacyIssue = pharmacy.issue;
    }
  }

  return medicationFailed || pharmacyIssue ? { medicationFailed, pharmacy: pharmacyIssue } : null;
}

export function describeRxPrefillIssue(issue: RxPrefillIssue): string {
  const failures: string[] = [];
  if (issue.medicationFailed) {
    failures.push('medication lookup failed');
  }
  if (issue.pharmacy === 'lookup-failed') {
    failures.push("the patient's pharmacies could not be loaded");
  }
  if (issue.pharmacy === 'not-found') {
    failures.push('no pharmacy found for patient');
  }
  return `Could not prepare prescription: ${failures.join('; ')}`;
}
